import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, post } from "../api";
import type { Demo } from "../types";
import { Button, Field, Note, Shell, inputClass, textareaClass } from "../ui";

export function NewDemoPage() {
  const navigate = useNavigate();
  const [website, setWebsite] = useState("");
  const [notes, setNotes] = useState("");
  const [kind, setKind] = useState("");
  const [name, setName] = useState("");
  const [phase, setPhase] = useState<"idle" | "creating">("idle");
  const [error, setError] = useState<string | null>(null);

  /*
   * Create the row, start the run, and hand the wait to the review screen, which shows the
   * progress panel while the row says `drafting`.
   *
   * THIS PAGE DELIBERATELY DOES NOT WATCH THE RUN any more. It used to, in a bare `for(;;)`
   * that was not tied to the component at all: reload the tab and the draft carried on with
   * nothing watching it, for ever. The wait belongs on the demo's own screen, which anyone
   * can reopen from the list.
   */
  async function start() {
    setError(null);
    setPhase("creating");
    let demoId = "";
    try {
      const created = await post<{ demo: Demo }>("/v1/demos", {
        website_url: website.trim(),
        ...(name.trim() ? { business_name: name.trim() } : {}),
        ...(kind.trim() ? { kind: kind.trim() } : {}),
        ...(notes.trim() ? { notes } : {}),
      });
      demoId = created.demo.id;
      await post(`/v1/demos/${demoId}/draft`, {});
    } catch (err) {
      const problem = err instanceof ApiError ? err : null;
      // No workflow bound (local dev without one): the row exists, review it by hand.
      if (problem?.status === 501 && demoId) {
        navigate(`/demos/${demoId}/review`);
        return;
      }
      setPhase("idle");
      setError(problem?.message ?? "Could not start the draft.");
      return;
    }
    navigate(`/demos/${demoId}/review`);
  }

  const busy = phase !== "idle";

  return (
    <Shell crumb>
      <main className="flex gap-8 lg:gap-12 px-4 sm:px-6 md:px-10 py-8 sm:py-10 grow flex-wrap">
        <form
          className="flex flex-col gap-7 w-full max-w-[640px]"
          onSubmit={(e) => { e.preventDefault(); if (!busy) void start(); }}
        >
          <div className="flex flex-col gap-2">
            <div className="text-[12px] text-muted uppercase tracking-[0.08em]">Step 1 of 3 · Start</div>
            <h1 className="m-0 text-[26px] font-semibold">Start from their website</h1>
            <div className="text-[14px] text-muted leading-relaxed">We read the site and draft the assistant. You check every fact before anyone can hear it. Usually about two minutes.</div>
          </div>

          <Field label="Website" hint="Google Maps or Facebook page works too if they have no site.">
            <input required type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" className={`${inputClass} h-[46px] font-mono`} />
          </Field>

          <Field label={<>What you already know <span className="font-normal text-muted">(optional)</span></>}
                 hint={<>Anything from the first call or the LinkedIn message. A "doesn't do X" here is worth more than a page of research.</>}>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className={textareaClass} />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={<>Kind of business <span className="font-normal text-muted">(optional)</span></>} hint="Free text: driving school, auto electrician, physio. Detected from the site when blank.">
              <input type="text" value={kind} onChange={(e) => setKind(e.target.value)} className={inputClass} />
            </Field>
            <Field label={<>Business name <span className="font-normal text-muted">(optional)</span></>} hint="Read from the site when blank.">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
            </Field>
          </div>

          {error && <div className="text-[13px] text-warn">{error}</div>}

          <div className="flex items-center gap-4 pt-2 flex-wrap">
            <Button kind="primary" type="submit" disabled={busy} className="h-[46px] px-5">
              {busy ? "Starting…" : "Build the draft"}
              {!busy && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>}
            </Button>
            <span className="text-[13px] text-muted">Nothing goes live from this step.</span>
          </div>
        </form>

        <aside className="flex flex-col gap-4 w-full max-w-[480px] lg:pt-11">
          <div className="flex flex-col gap-3.5 px-6 py-5 bg-paper border border-line rounded-card">
            <div className="text-[13px] font-semibold">What the draft will contain</div>
            <div className="flex flex-col gap-2.5 text-[13px] leading-relaxed text-ink-soft">
              {[
                ["What it can tell callers.", "Hours, area, services, published prices. Each with the page it came from and the words on it."],
                ["Reasons people call.", "What callers ring about, what the assistant asks them, and what the business does afterwards."],
                ["Things it will not offer.", "The near-misses the site never mentions - the things people assume they do. You confirm or cut them."],
                ["Voice and demo page.", "Opening line, assistant name, brand colours and logo pulled from the site."],
              ].map(([head, body], i) => (
                <div key={head} className="flex gap-2.5"><span className="text-ok font-semibold">{i + 1}</span><span><b className="font-medium">{head}</b> {body}</span></div>
              ))}
            </div>
          </div>
          <Note title="What it will never do on its own">
            Quote a price the site does not publish. Offer a service nobody wrote down. Book into a calendar. Those come with the bespoke build once they are interested.
          </Note>
        </aside>
      </main>
    </Shell>
  );
}
